import type {
  ContextSessionResponse,
  OrganizationFrame,
  RelationshipStatus,
} from "@kontextwerk/control-web-client"
import type { UserMember } from "../_shared/_logic/user-types"
import type { OwnMembership } from "../_shared/_logic/member-access"

const relationshipStatus = (value: unknown): RelationshipStatus | null =>
  value === "active" || value === "suspended" ? value : null

/** The confirmed context's own relationship, including the represented member during support. */
export const resolveOwnMembership = (
  session: ContextSessionResponse | null | undefined,
): OwnMembership | null => {
  const context = session?.context

  if (!session || context?.kind !== "organization") return null

  return {
    membershipId: context.membership_id,
    userId: context.delegation?.represented_user_id ?? session.user.id,
    organizationId: context.organization_id,
    roleId: context.role_id,
    roleName: context.role_name,
    revision: context.membership_revision,
  }
}

/**
 * Member labels of the target user from the confirmed organization roster. The own relationship
 * also resolves from the context when the roster omits it; another tenant's roster never does.
 */
export const resolveUserMember = (
  organization: OrganizationFrame | null | undefined,
  userId: string | null | undefined,
  session: ContextSessionResponse | null | undefined,
): UserMember | null => {
  if (!organization || !userId) return null

  const entry = (organization.users ?? []).find(
    (candidate) => candidate.organization_id === organization.id && candidate.id === userId,
  )

  if (entry)
    return {
      userId: entry.id,
      membershipId: entry.membership_id,
      username: entry.username,
      firstName: entry.first_name,
      lastName: entry.last_name,
      profileImageUrl: entry.profile_image_url ?? null,
      membershipStatus: relationshipStatus(
        (entry as { membership_status?: unknown }).membership_status,
      ),
    }

  const own = resolveOwnMembership(session)

  if (
    !own ||
    own.userId !== userId ||
    own.organizationId.toLowerCase() !== organization.id.toLowerCase() ||
    session?.context.kind !== "organization" ||
    session.context.delegation
  )
    return null

  return {
    userId,
    membershipId: own.membershipId,
    username: session.user.username,
    firstName: session.user.first_name,
    lastName: session.user.last_name,
    profileImageUrl: session.user.profile_image_url ?? null,
    membershipStatus: "active",
  }
}
